import { Plugin, icons } from '@ckeditor/ckeditor5-core';
import { ButtonView, DropdownButtonView, Model, createDropdown, addListToDropdown } from '@ckeditor/ckeditor5-ui';
import { CKEditorError, Collection } from '@ckeditor/ckeditor5-utils';
import MediaResizeEditing from "./mediaresizeediting";
const RESIZE_ICONS = {
    small: icons.objectSizeSmall,
    medium: icons.objectSizeMedium,
    large: icons.objectSizeLarge,
    original: icons.objectSizeFull
};
const RESIZE_OPTIONS = {
    resizeUnit: '%',
    resizeOptions: [
        {
            name: 'resizeVideo:original',
            value: null,
            icon: 'original'
        },
        {
            name: 'resizeMedia:50',
            value: '50',
            icon: 'medium'
        },
        {
            name: 'resizeMedia:75',
            value: '75',
            icon: 'large'
        },
        {
            name: 'resizeMedia:100',
            value: '100',
            icon: 'large'
        }
    ]
};
export default class MediaResizeButtons extends Plugin {
    /**
     * @inheritDoc
     */
    static get requires() {
        return [MediaResizeEditing];
    }
    /**
     * @inheritDoc
     */
    static get pluginName() {
        return 'MediaResizeButtons';
    }
    /**
     * @inheritDoc
     */
    constructor(editor) {
        super(editor);
        this._resizeUnit = RESIZE_OPTIONS.resizeUnit;
    }
    /**
     * @inheritDoc
     */
    init() {
        const editor = this.editor;
        const options = RESIZE_OPTIONS.resizeOptions;
        const command = editor.commands.get('resizeMedia');
        this.bind('isEnabled').to(command);
        for (const option of options) {
            this._registerMediaResizeButton(option);
        }
        this._registerMediaResizeDropdown(options);
    }
    _registerMediaResizeButton(option) {
        const editor = this.editor;
        const { name, value, icon } = option;
        const optionValueWithUnit = value ? value + this._resizeUnit : null;
        editor.ui.componentFactory.add(name, locale => {
            const button = new ButtonView(locale);
            const command = editor.commands.get('resizeMedia');
            const labelText = this._getOptionLabelValue(option, true);
            if (!RESIZE_ICONS[icon]) {
                throw new CKEditorError('mediaresizebuttons-missing-icon', editor, option);
            }
            button.set({
                // Use the `label` property for a verbose description (because of ARIA).
                label: labelText,
                icon: RESIZE_ICONS[icon],
                tooltip: labelText,
                isToggleable: true
            });
            // Bind button to the command.
            button.bind('isEnabled').to(this);
            button.bind('isOn').to(command, 'value', getIsOnButtonCallback(optionValueWithUnit));
            this.listenTo(button, 'execute', () => {
                editor.execute('resizeMedia', { width: optionValueWithUnit });
            });
            return button;
        });
    }
    _registerMediaResizeDropdown(options) {
        const editor = this.editor;
        const t = editor.t;
        const originalSizeOption = options.find(option => !option.value);
        // Register dropdown.
        const componentCreator = (locale) => {
            const command = editor.commands.get('resizeMedia');
            const dropdownView = createDropdown(locale, DropdownButtonView);
            const dropdownButton = dropdownView.buttonView;
            dropdownButton.set({
                tooltip: t('Resize video'),
                commandValue: originalSizeOption.value,
                icon: RESIZE_ICONS.medium,
                isToggleable: true,
                label: this._getOptionLabelValue(originalSizeOption),
                withText: true,
                class: 'ck-resize-media-button'
            });
            dropdownButton.bind('label').to(command, 'value', commandValue => {
                if (commandValue && commandValue.width) {
                    return commandValue.width;
                }
                else {
                    return this._getOptionLabelValue(originalSizeOption);
                }
            });
            //TODO: check more
            // dropdownView.bind('isOn').to(command);
            dropdownView.bind('isEnabled').to(this);
            addListToDropdown(dropdownView, () => this._getResizeDropdownListItemDefinitions(options, command), {
                ariaLabel: t('Video resize list'),
                role: 'menu'
            });
            // Execute command when an item from the dropdown is selected.
            this.listenTo(dropdownView, 'execute', evt => {
                editor.execute(evt.source.commandName, { width: evt.source.commandValue });
                editor.editing.view.focus();
            });
            return dropdownView;
        };
        editor.ui.componentFactory.add('resizeMedia', componentCreator);
        editor.ui.componentFactory.add('mediaResize', componentCreator);
    }
    _getOptionLabelValue(option, forTooltip = false) {
        const t = this.editor.t;
        if (option.label) {
            return option.label;
        }
        else if (forTooltip) {
            if (option.value) {
                return t('Resize video to %0', option.value + this._resizeUnit);
            }
            else {
                return t('Resize video to the original size');
            }
        }
        else {
            if (option.value) {
                return option.value + this._resizeUnit;
            }
            else {
                return t('Original');
            }
        }
    }
    _getResizeDropdownListItemDefinitions(options, command) {
        const itemDefinitions = new Collection();
        options.map(option => {
            const optionValueWithUnit = option.value ? option.value + this._resizeUnit : null;
            const definition = {
                type: 'button',
                model: new Model({
                    commandName: 'resizeMedia',
                    commandValue: optionValueWithUnit,
                    label: this._getOptionLabelValue(option),
                    withText: true,
                    icon: null
                })
            };
            definition.model.bind('isOn').to(command, 'value', getIsOnButtonCallback(optionValueWithUnit));
            itemDefinitions.add(definition);
        });
        return itemDefinitions;
    }
}
function getIsOnButtonCallback(value) {
    return (commandValue) => {
        const objectCommandValue = commandValue;
        if (value === null && objectCommandValue === value) {
            return true;
        }
        return objectCommandValue !== null && objectCommandValue.width === value;
    };
}
//# sourceMappingURL=mediaresizebuttons.js.map